import React, { useEffect, useState } from 'react';
import { DialogueMessage as DialogueMessageType } from '../../services/conversationService';

interface DialogueMessageProps {
  message: DialogueMessageType;
  isLatest: boolean;
  skipTyping?: boolean;
}

const DialogueMessage: React.FC<DialogueMessageProps> = ({ message, isLatest, skipTyping = false }) => {
  const [visible, setVisible] = useState(!isLatest);
  const [textContent, setTextContent] = useState('');
  const [typingComplete, setTypingComplete] = useState(false);
  
  // Format text to ensure first letter is not cut off
  const formatText = (text: string) => {
    if (!text) return '';
    // Ensure there's a space at the start if it doesn't already have one
    return text.startsWith(' ') ? text : ' ' + text;
  };
  
  // Animate entrance for the latest message
  useEffect(() => {
    if (isLatest) {
      // Delay showing the message slightly
      const timer = setTimeout(() => {
        setVisible(true);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isLatest]);
  
  // Typing effect for character and narrator text
  useEffect(() => {
    if (!visible || message.speaker === 'player' || skipTyping) {
      // Player messages or skip typing - show immediately
      setTextContent(formatText(message.text));
      setTypingComplete(true);
      return;
    }
    
    const fullText = formatText(message.text);
    let index = 0;
    setTextContent('');
    setTypingComplete(false);
    
    // Faster typing speed for narrator, slower for character
    const typingSpeed = message.speaker === 'narrator' ? 30 : 40;
    
    const typingInterval = setInterval(() => {
      if (index < fullText.length) {
        setTextContent(current => current + fullText.charAt(index));
        index++;
      } else {
        clearInterval(typingInterval);
        setTypingComplete(true);
      }
    }, typingSpeed);
    
    return () => clearInterval(typingInterval);
  }, [visible, message, skipTyping]);
  
  if (!visible) return null;
  
  const getMessageClass = () => {
    switch (message.speaker) {
      case 'character':
        return 'character-message';
      case 'player':
        return 'player-message';
      case 'narrator':
        return 'narrator-message';
      default:
        return '';
    }
  };
  
  // Function to render text content based on speaker
  const renderTextContent = () => {
    return (
      <span className="dialogue-text-content">
        {textContent}
      </span>
    );
  };
  
  return (
    <div className={`dialogue-message-container ${getMessageClass()} ${isLatest ? 'latest-message' : ''}`}>
      {message.speaker === 'character' && message.character && (
        <div className="dialogue-speaker">
          <div className="character-portrait">
            {message.imageUrl ? (
              <img 
                src={message.imageUrl} 
                alt={message.character} 
                className="character-image"
              />
            ) : (
              <div className="character-placeholder">{message.character.charAt(0)}</div>
            )}
          </div>
          <span className="character-name">{message.character}</span>
        </div>
      )}
      
      <div className={`dialogue-bubble ${message.speaker}-bubble`}>
        <p className="dialogue-text">
          {renderTextContent()}
          {!typingComplete && <span className="typing-cursor">|</span>}
        </p>
      </div>
    </div>
  );
};

export default DialogueMessage; 